import { Block, MousePosition, State } from "../../types";
import { isMouseOnPage } from "../util";

export type MouseDownAction = Readonly<{
  ctrlClick: boolean;
  position: MousePosition;
  type: "MOUSE DOWN";
  shiftClick: boolean;
}>;

export const mouseDown = (state: State, action: MouseDownAction): State => {
  const { ctrlClick, position, shiftClick } = action;
  if (isMouseOnPage(position)) {
    const selectedBlockId = Object.keys(state.activeBlocks).find(k => {
      const block = state.activeBlocks[k];
      return (
        position.page === block.page &&
        position.x >= block.minX &&
        position.x <= block.maxX &&
        position.y >= block.minY &&
        position.y <= block.maxY
      );
    });
    if (!selectedBlockId) {

      if (ctrlClick || shiftClick) {
        return state;
      }
      return {
        ...state,
        selectedBlockId: undefined,
      };
    }
    let blocks = state.blocks;
    let activeBlocks = state.activeBlocks;

    if (!state.readonly && state.selectedBlockId && shiftClick) {
      const clickedBlock = blocks[selectedBlockId];


      const previousSelectedBlock = blocks[state.selectedBlockId];
      const continuationBlockSequence = [previousSelectedBlock];

      let nextBlockId = previousSelectedBlock.continuationId;
      let prevBlockId = previousSelectedBlock.continuedFromId;
      while (nextBlockId) {
        continuationBlockSequence.push(blocks[nextBlockId])
        nextBlockId = blocks[nextBlockId].continuationId;
      }
      while (prevBlockId) {
        continuationBlockSequence.unshift(blocks[prevBlockId]);
        prevBlockId = blocks[prevBlockId].continuedFromId;
      } 
      if (continuationBlockSequence.find(x => x === clickedBlock)) {
        ClearContinuationBlock(blocks, selectedBlockId);
        ClearContinuationBlock(activeBlocks, selectedBlockId);
      } else {
        let clickedIndex = state.activeBlockOrder.findIndex(x => x === selectedBlockId);
        let firstId = continuationBlockSequence[0].id
        let lastId = continuationBlockSequence[continuationBlockSequence.length - 1].id
        let firstIndex = state.activeBlockOrder.findIndex(x => x === firstId);
        let lastIndex = state.activeBlockOrder.findIndex(x => x === lastId);

        if (clickedIndex < firstIndex && !clickedBlock.continuationId) {
          SetContinuationBlock(blocks, selectedBlockId, firstId);
          SetContinuationBlock(activeBlocks, selectedBlockId, firstId);
        } 
        if (clickedIndex > lastIndex && !clickedBlock.continuedFromId) {
          SetContinuationBlock(blocks, lastId, selectedBlockId );
          SetContinuationBlock(activeBlocks, lastId, selectedBlockId);
        }
      }
    }
    if (!state.readonly && state.showReadingOrder && ctrlClick && state.selectedBlockId) {
      const selectedIndex = state.blockOrder.indexOf(selectedBlockId);
      const currentIndex = state.blockOrder.indexOf(state.selectedBlockId);
      const activeSelectedIndex = state.activeBlockOrder.indexOf(selectedBlockId);
      const activeCurrentIndex = state.activeBlockOrder.indexOf(state.selectedBlockId);
      if (selectedIndex >= 0 && currentIndex >= 0) {
        let blockOrder = state.blockOrder;
        let activeBlockOrder = state.activeBlockOrder;
        if (selectedIndex === currentIndex) {
          blockOrder = SetBlockToStartBlockOrder(state.blockOrder, selectedIndex);
          activeBlockOrder = SetBlockToStartBlockOrder(state.activeBlockOrder, activeSelectedIndex);
        } else if (selectedIndex > currentIndex) {
          blockOrder = RewindBlockInBlockOrder(state.blockOrder, selectedIndex, currentIndex);
          activeBlockOrder = RewindBlockInBlockOrder(state.activeBlockOrder, activeSelectedIndex, activeCurrentIndex);
        } else {
          blockOrder = AdvanceBlockInBlockOrder(state.blockOrder, selectedIndex, currentIndex);
          activeBlockOrder = AdvanceBlockInBlockOrder(state.activeBlockOrder, activeSelectedIndex, activeCurrentIndex);
        }
        return {
          ...state,
          blockOrder,
          scrollToTextBlock: selectedBlockId,
          selectedBlockId,
          activeBlocks,
          activeBlockOrder,
        };
      }
    }
    return {
      ...state,
      blocks,
      scrollToTextBlock: selectedBlockId,
      selectedBlockId,
    };
  }
  if (state.selectedBlockId) {
    return {
      ...state,
      selectedBlockId: undefined,
    };
  }
  return state;
};

const AdvanceBlockInBlockOrder = (blockOrder: readonly string[], selectedIndex: number, currentIndex: number): readonly string[] => {
  blockOrder = [
    ...blockOrder.slice(0, selectedIndex),
    ...blockOrder.slice(selectedIndex + 1, currentIndex),
    blockOrder[currentIndex],
    blockOrder[selectedIndex],
    ...blockOrder.slice(currentIndex + 1),
  ];
  return blockOrder;
};

const RewindBlockInBlockOrder = (blockOrder: readonly string[], selectedIndex: number, currentIndex: number): readonly string[] => {
  blockOrder = [
    ...blockOrder.slice(0, currentIndex),
    blockOrder[currentIndex],
    blockOrder[selectedIndex],
    ...blockOrder.slice(currentIndex + 1, selectedIndex),
    ...blockOrder.slice(selectedIndex + 1),
  ];
  return blockOrder;
};

const SetBlockToStartBlockOrder = (blockOrder: readonly string[], selectedIndex: number): readonly string[] => {
  blockOrder = [
    blockOrder[selectedIndex],
    ...blockOrder.slice(0, selectedIndex),
    ...blockOrder.slice(selectedIndex + 1),
  ];
  return blockOrder;
};

const SetContinuationBlock = (blocks: { [blockId: string]: Block }, previousBlockId: string, selectedBlockId: string) => {
  blocks[previousBlockId] = {
    ...blocks[previousBlockId],
    continuationId: blocks[selectedBlockId].id,
  };
  blocks[selectedBlockId] = {
    ...blocks[selectedBlockId],
    continuedFromId: previousBlockId,
  };
};

const ClearContinuationBlock = (blocks: { [blockId: string]: Block }, blockId: string) => {
  const previous = blocks[blockId].continuedFromId;
  const next = blocks[blockId].continuationId;

  blocks[blockId] = {
    ...blocks[blockId],
    continuationId: "",
    continuedFromId: "",
  };
  if (previous) {
    blocks[previous] = {
      ...blocks[previous],
      continuationId: ""
    }
  }
  if (next) {
    blocks[next] = {
      ...blocks[next],
      continuedFromId: ""
    }
  }
};